import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import {
  isUserOptionSelected,
  selectedOptionIds,
} from '@/components/assessments/assessmentResponse';
import { QuizFileDropzone } from '@/components/assessments/QuizFileDropzone';
import { quizFileLabel } from '@/components/assessments/quizFileAnswer';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import {
  AttemptStaffFlags,
  attemptEventLabel,
  hasPendingLateEvents,
} from '@/components/assessments/AttemptStaffFlags';
import { formatScorePercentage, showsScoreAndPercent } from '@/lib/assessment-score-display';

interface ReviewOption {
  id: string;
  label?: string;
  content: string;
  isCorrect?: boolean;
}

interface AttemptReviewPayload {
  attempt: {
    id: string;
    status: string;
    startedAt: string;
    submittedAt: string | null;
    score: number | null;
    deadlineAt?: string | null;
    submissionSource?: string | null;
    late?: boolean;
  };
  events?: Array<{
    id: string;
    type: string;
    createdAt: string;
  }>;
  assessment: {
    id?: string;
    title: string;
    code?: string;
    currentVersion?: {
      settings?: { type?: string; require_passing_score?: boolean; show_score_and_percent?: boolean } | null;
    };
  };
  review: {
    maxScore: number;
    result: 'pass' | 'fail' | null;
    showAnswers: boolean;
    pendingReview?: boolean;
    correctionMode?: 'automatic' | 'manual';
  } | null;
  responses: Array<{
    questionId: string;
    response: unknown;
    isCorrect: boolean | null;
    score: number | null;
  }>;
  questions: Array<{
    id: string;
    stem: string;
    type?: string;
    requiresReview?: boolean;
    currentVersion?: { options?: ReviewOption[] };
  }>;
}

interface AssessmentAttemptReviewProps {
  attemptId: string;
  isAdminPreview?: boolean;
  backTo: string;
  backLabel?: string;
  onBack: () => void;
  attemptNumber?: number;
  titlePrefix?: string;
  canManageAttempts?: boolean;
  initialEditMode?: boolean;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatUserAnswer(
  question: { type?: string; currentVersion?: { options?: ReviewOption[] } },
  response: unknown,
): string {
  if (response === null || response === undefined || response === '') return 'Sin respuesta';
  if (question.type === 'file_upload') return quizFileLabel(response) ?? 'Sin archivo';
  if (question.type === 'true_false') {
    return String(response) === 'true' ? 'Verdadero' : 'Falso';
  }
  const options = question.currentVersion?.options ?? [];
  if (question.type === 'multiple_choice' || Array.isArray(response)) {
    const ids = selectedOptionIds(response);
    if (ids.length === 0) return 'Sin respuesta';
    return ids
      .map((id) => options.find((option) => option.id === id)?.content ?? id)
      .join(', ');
  }
  const match = options.find((option) => option.id === String(response));
  return match?.content ?? String(response);
}

export function AssessmentAttemptReview({
  attemptId,
  isAdminPreview = false,
  backTo,
  backLabel = 'Volver al curso',
  onBack,
  attemptNumber,
  titlePrefix = '',
  canManageAttempts = false,
  initialEditMode = false,
}: AssessmentAttemptReviewProps) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canInspectAttempts =
    canManageAttempts ||
    hasPermission(PERMISSIONS.EVALUACIONES_VER) ||
    hasPermission(PERMISSIONS.CURSOS_SUPERVISAR);
  const [editing, setEditing] = useState(initialEditMode && canManageAttempts);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [scoreOverride, setScoreOverride] = useState<string>('');
  const [reviewComment, setReviewComment] = useState('');

  const queryKey = [
    'assessment-attempt-review',
    canInspectAttempts ? 'manage' : isAdminPreview ? 'admin' : 'student',
    attemptId,
  ] as const;

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => {
      if (canInspectAttempts) {
        return api<AttemptReviewPayload>(`/assessments/attempts/${attemptId}/manage`);
      }
      if (isAdminPreview) {
        return api<AttemptReviewPayload>(`/assessments/attempts/${attemptId}`);
      }
      return api<AttemptReviewPayload>(`/student/assessment-attempts/${attemptId}`);
    },
    enabled: Boolean(attemptId),
    staleTime: 0,
  });

  useEffect(() => {
    if (!data) return;
    const map: Record<string, unknown> = {};
    data.responses.forEach((row) => {
      map[row.questionId] = row.response;
    });
    setAnswers(map);
    setScoreOverride(String(data.attempt.score ?? 0));
  }, [data]);

  useEffect(() => {
    setEditing(initialEditMode && canManageAttempts);
    setReviewComment('');
  }, [initialEditMode, attemptId, canManageAttempts]);

  const incorporateMutation = useMutation({
    mutationFn: () =>
      api(`/assessments/attempts/${attemptId}`, {
        method: 'PATCH',
        body: { incorporateLateAnswers: true },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const parsedScore = Number.parseFloat(scoreOverride);
      const body: { responses: { questionId: string; response: unknown }[]; score?: number } = {
        responses: Object.entries(answers).map(([questionId, response]) => ({ questionId, response })),
      };
      if (!Number.isNaN(parsedScore)) {
        body.score = parsedScore;
      }
      return api(`/assessments/attempts/${attemptId}`, { method: 'PATCH', body });
    },
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const manualMutation = useMutation({
    mutationFn: (input: { questionId: string; isCorrect: boolean }) =>
      api(`/assessments/attempts/${attemptId}`, {
        method: 'PATCH',
        body: { manualGrade: input },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      api(`/assessments/attempts/${attemptId}/quick-review`, {
        method: 'POST',
        body: { decision, comment: reviewComment.trim() },
      }),
    onSuccess: () => {
      setReviewComment('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });
  const responseMap = new Map(data?.responses.map((row) => [row.questionId, row]) ?? []);
  const review = data?.review;
  const showGrades = showsScoreAndPercent(data?.assessment.currentVersion?.settings);
  const score = data?.attempt.score ?? 0;
  const maxScore = review?.maxScore ?? data?.questions.length ?? 0;
  const editedScore = Number.parseFloat(scoreOverride);
  const percentScore = editing ? (Number.isNaN(editedScore) ? null : editedScore) : data?.attempt.score ?? null;

  return (
    <div className="lms-quiz-attempt font-sans">
      <div className="lms-site-admin-hub-container py-8 md:py-12">
        <header className="lms-quiz-attempt__header">
          <HubBackLink label="Volver a intentos" onBack={onBack} />
          <div className="lms-quiz-attempt__header-copy">
            <h1>
              {titlePrefix}
              {data?.assessment.title ?? 'Evaluación'}
            </h1>
            {data && (
              <p>
                <span className="material-symbols-outlined" aria-hidden="true">assignment</span>
                Intento {attemptNumber ?? '—'} · {data.assessment.code ?? '—'}
              </p>
            )}
          </div>
        </header>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader label="Cargando intento…" />
          </div>
        ) : error ? (
          <Alert variant="danger">{(error as Error).message}</Alert>
        ) : data ? (
          <>
            <section className="lms-quiz-review__summary">
              <div className="lms-quiz-review__summary-grid">
                {showGrades && (
                <div>
                  <span className="lms-quiz-review__summary-label">Calificación</span>
                  {review?.pendingReview && !editing ? (
                    <strong>Pendiente</strong>
                  ) : editing && canManageAttempts ? (
                    <div className="lms-quiz-review__score-edit">
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        max={maxScore}
                        value={scoreOverride}
                        onChange={(event) => setScoreOverride(event.target.value)}
                        className="lms-quiz-review__score-input"
                        aria-label="Calificación"
                      />
                      <span>/ {maxScore.toFixed(1)}</span>
                    </div>
                  ) : (
                    <strong>{score.toFixed(1)} / {maxScore.toFixed(1)}</strong>
                  )}
                </div>
                )}
                {showGrades && (
                <div>
                  <span className="lms-quiz-review__summary-label">%</span>
                  <strong>
                    {review?.pendingReview && !editing ? '—' : formatScorePercentage(percentScore, maxScore)}
                  </strong>
                </div>
                )}
                <div>
                  <span className="lms-quiz-review__summary-label">Resultado</span>
                  {review?.pendingReview && (
                    <span className="lms-quiz-review__badge lms-quiz-review__badge--pending">En revisión</span>
                  )}
                  {review?.result === 'pass' && (
                    <span className="lms-quiz-review__badge lms-quiz-review__badge--pass">Aprobado</span>
                  )}
                  {review?.result === 'fail' && (
                    <span className="lms-quiz-review__badge lms-quiz-review__badge--fail">Reprobado</span>
                  )}
                  {review?.result === null && !review.pendingReview && <strong>—</strong>}
                </div>
                <div>
                  <span className="lms-quiz-review__summary-label">Enviado</span>
                  <strong>{formatDate(data.attempt.submittedAt)}</strong>
                </div>
              </div>
              <AttemptStaffFlags
                late={data.attempt.late}
                submissionSource={data.attempt.submissionSource}
                hasPendingLateAnswers={hasPendingLateEvents(data.events)}
                multiDevice={data.events?.some((event) => event.type === 'multi_device')}
                hasConnectionGaps={data.events?.some((event) => event.type === 'connection_gaps')}
              />
              {canManageAttempts && hasPendingLateEvents(data.events) && (
                <div className="lms-quiz-review__actions">
                  <button
                    type="button"
                    className="lms-quiz-review__action-btn lms-quiz-review__action-btn--primary"
                    onClick={() => incorporateMutation.mutate()}
                    disabled={incorporateMutation.isPending}
                  >
                    {incorporateMutation.isPending ? 'Incorporando…' : 'Incorporar respuestas tardías'}
                  </button>
                </div>
              )}
              {data.events && data.events.length > 0 && (
                <ol className="lms-attempt-events">
                  {data.events.map((event) => (
                    <li key={event.id}>
                      <span>{attemptEventLabel(event.type)}</span>
                      <span>{formatDate(event.createdAt)}</span>
                    </li>
                  ))}
                </ol>
              )}
              {canManageAttempts && editing && (
                <div className="lms-quiz-review__actions">
                  <button
                    type="button"
                    className="lms-quiz-review__action-btn lms-quiz-review__action-btn--primary"
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending}
                  >
                    {saveMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
                  </button>
                  <button
                    type="button"
                    className="lms-quiz-review__action-btn"
                    onClick={() => {
                      setEditing(false);
                      const map: Record<string, unknown> = {};
                      data.responses.forEach((row) => {
                        map[row.questionId] = row.response;
                      });
                      setAnswers(map);
                      setScoreOverride(String(data.attempt.score ?? 0));
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              )}
              {saveMutation.error && (
                <Alert variant="danger" className="mt-3">
                  {(saveMutation.error as Error).message}
                </Alert>
              )}
              {canManageAttempts && !editing && data.attempt.status === 'pending_review' && (
                <div className="lms-quiz-review__finalize">
                  <span className="lms-quiz-review__summary-label">Finalizar revisión</span>
                  <p>Aprobar desbloquea el curso. Rechazar lo deja cerrado.</p>
                  <label className="lms-quiz-review__finalize-label" htmlFor="review-finalize-comment">
                    Comentario
                  </label>
                  <textarea
                    id="review-finalize-comment"
                    rows={3}
                    value={reviewComment}
                    onChange={(event) => setReviewComment(event.target.value)}
                    placeholder="Obligatorio si rechazas el examen"
                  />
                  <div className="lms-quiz-review__actions">
                    <button
                      type="button"
                      className="lms-quiz-review__action-btn lms-quiz-review__action-btn--danger"
                      onClick={() => finalizeMutation.mutate('rejected')}
                      disabled={finalizeMutation.isPending || reviewComment.trim().length === 0}
                    >
                      Rechazar
                    </button>
                    <button
                      type="button"
                      className="lms-quiz-review__action-btn lms-quiz-review__action-btn--primary"
                      onClick={() => finalizeMutation.mutate('approved')}
                      disabled={finalizeMutation.isPending}
                    >
                      {finalizeMutation.isPending ? 'Guardando…' : 'Aprobar'}
                    </button>
                  </div>
                  {finalizeMutation.error && (
                    <Alert variant="danger">
                      {(finalizeMutation.error as Error).message}
                    </Alert>
                  )}
                </div>
              )}
            </section>

            <div className="lms-quiz-review__questions">
              {data.questions.map((question, index) => {
                const row = responseMap.get(question.id);
                const options = question.currentVersion?.options ?? [];
                const userResponse = editing ? answers[question.id] : row?.response;
                const showAnswers = review?.showAnswers ?? false;
                const awaitsManualMark =
                  row != null &&
                  row.score == null &&
                  (question.requiresReview || review?.correctionMode === 'manual');
                const trueOption =
                  options.find((option) => option.content.toLowerCase().includes('verdadero')) ??
                  options[0];
                const correctIsTrue = Boolean(trueOption?.isCorrect);

                return (
                  <article key={question.id} className="lms-quiz-attempt__card">
                    <div className="lms-quiz-question__label">
                      Pregunta <span>{index + 1}</span>
                      {!editing && awaitsManualMark && (
                        <span className="lms-quiz-review__question-badge lms-quiz-review__question-badge--pending">
                          Pendiente
                        </span>
                      )}
                      {!editing && row?.isCorrect === true && (
                        <span className="lms-quiz-review__question-badge lms-quiz-review__question-badge--ok">
                          Correcta
                        </span>
                      )}
                      {!editing && row?.isCorrect === false && (
                        <span className="lms-quiz-review__question-badge lms-quiz-review__question-badge--bad">
                          Incorrecta
                        </span>
                      )}
                    </div>
                    <div className="lms-quiz-question__stem">{question.stem}</div>

                    {canManageAttempts && !editing && awaitsManualMark && (
                      <div className="lms-quiz-review__manual">
                        <button
                          type="button"
                          className="lms-quiz-review__action-btn lms-quiz-review__action-btn--primary"
                          disabled={manualMutation.isPending}
                          onClick={() => manualMutation.mutate({ questionId: question.id, isCorrect: true })}
                        >
                          Correcta
                        </button>
                        <button
                          type="button"
                          className="lms-quiz-review__action-btn"
                          disabled={manualMutation.isPending}
                          onClick={() => manualMutation.mutate({ questionId: question.id, isCorrect: false })}
                        >
                          Incorrecta
                        </button>
                      </div>
                    )}

                    {row && !editing && question.type !== 'file_upload' && (
                      <p className="lms-quiz-review__answered-line">
                        Respuesta registrada:{' '}
                        <strong>{formatUserAnswer(question, userResponse)}</strong>
                      </p>
                    )}

                    {question.type === 'file_upload' ? (
                      <QuizFileDropzone name={`review-file-${question.id}`} value={userResponse} readOnly />
                    ) : question.type === 'short_answer' || question.type === 'long_answer' ? (
                      editing ? (
                        question.type === 'long_answer' ? (
                          <textarea
                            className="lms-quiz-question__text-input"
                            rows={5}
                            value={typeof userResponse === 'string' ? userResponse : ''}
                            onChange={(event) =>
                              setAnswers((prev) => ({ ...prev, [question.id]: event.target.value }))
                            }
                          />
                        ) : (
                          <input
                            type="text"
                            className="lms-quiz-question__text-input"
                            value={typeof userResponse === 'string' ? userResponse : ''}
                            onChange={(event) =>
                              setAnswers((prev) => ({ ...prev, [question.id]: event.target.value }))
                            }
                          />
                        )
                      ) : row ? null : (
                        <p className="lms-quiz-review__text-answer">Sin respuesta</p>
                      )
                    ) : question.type === 'true_false' || options.length > 0 ? (
                      <fieldset className="lms-quiz-question__options" disabled={!editing}>
                        {(question.type === 'true_false'
                          ? [
                              { id: 'true', content: 'Verdadero' },
                              { id: 'false', content: 'Falso' },
                            ]
                          : options).map((option) => {
                            const optionId = option.id;
                            const multiple = question.type === 'multiple_choice';
                            const isSelected = isUserOptionSelected(option, userResponse);
                            const isCorrectOption =
                              showAnswers &&
                              (question.type === 'true_false'
                                ? (optionId === 'true' && correctIsTrue) ||
                                  (optionId === 'false' && !correctIsTrue)
                                : 'isCorrect' in option && option.isCorrect);

                            let stateClass = '';
                            if (!editing && isSelected && row?.isCorrect === true)
                              stateClass = ' lms-quiz-review__option--correct';
                            else if (!editing && isSelected && row?.isCorrect === false)
                              stateClass = ' lms-quiz-review__option--incorrect';
                            else if (!editing && showAnswers && isCorrectOption)
                              stateClass = ' lms-quiz-review__option--correct-hint';

                            return (
                              <label
                                key={optionId}
                                className={`lms-quiz-question__option lms-quiz-review__option${isSelected ? ' lms-quiz-review__option--selected' : ''}${stateClass}`}
                              >
                                <input
                                  type={multiple ? 'checkbox' : 'radio'}
                                  className="lms-quiz-question__input"
                                  name={multiple ? `review-${question.id}-${optionId}` : `review-${question.id}`}
                                  value={optionId}
                                  checked={isSelected}
                                  readOnly={!editing}
                                  onChange={() => {
                                    if (!editing) return;
                                    if (!multiple) {
                                      setAnswers((prev) => ({ ...prev, [question.id]: optionId }));
                                      return;
                                    }
                                    setAnswers((prev) => {
                                      const current = selectedOptionIds(prev[question.id]);
                                      const next = current.includes(optionId)
                                        ? current.filter((id) => id !== optionId)
                                        : [...current, optionId];
                                      return { ...prev, [question.id]: next };
                                    });
                                  }}
                                />
                                <span className="lms-quiz-question__option-text">
                                  {option.content}
                                </span>
                                {!editing && isSelected && (
                                  <span className="lms-quiz-review__option-tag">Tu respuesta</span>
                                )}
                                {!editing && showAnswers && isCorrectOption && !isSelected && (
                                  <span className="lms-quiz-review__option-tag">Correcta</span>
                                )}
                              </label>
                            );
                          })}
                      </fieldset>
                    ) : (
                      <p className="lms-quiz-review__text-answer">
                        {formatUserAnswer(question, userResponse)}
                      </p>
                    )}
                  </article>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
